import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { describeApiError, isApiError } from "../../api/errors";
import {
  useAcceptFxOpeningEstimate,
  useRestoreFxOpeningAutoFigure,
  useCapabilities,
  useConsent,
  useFxOpeningBalances,
  useMigration,
  useProfile,
  useAllReviewItems,
  useResolveReviewItem,
  useRun,
  useScope,
  useSetFxOpeningBalance,
  useSignoff,
  useSignoffWatch,
  useSystems,
  useUpdateConversionBasis,
  useUpdateScope,
} from "../../api/hooks";
import type { MigrationState, PreflightItem, Severity } from "../../api/types";
import { formatDate } from "../../api/types";
import { ActionsFirst } from "../../components/ActionsFirst";
import { Badge } from "../../components/Badge";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Disclosure } from "../../components/Disclosure";
import { Field } from "../../components/Field";
import { SegmentedTabs } from "../../components/SegmentedTabs";
import { Spinner } from "../../components/Spinner";
import { SystemWarningBanner } from "../../components/SystemWarningBanner";
import { Table } from "../../components/Table";
import { useConnections } from "../../api/hooks";
import { vendorName } from "../../i18n/oauthCopy";
import { t } from "../../i18n/t";
import { MigrationPage } from "../migrationPage";
import { isStageActive } from "../stageRoute";
import { describeConnection } from "../connect/ConnectScreen";
import { Concepts, type ConceptRow } from "../dryrun/Concepts";
import { resolveMayMoveState } from "../review/gating";
import { OtherReviewItems } from "../review/OtherReviewItems";
import { isOpen } from "../review/suggestions";
import { ApiBudgetNotice, groupCalls } from "./ApiBudgetNotice";
import { JournalsChoice } from "./JournalsChoice";
import { EntityToggles, type CapabilityEntry } from "./EntityToggles";
import { FxOpeningBalances } from "./FxOpeningBalances";
import { SourceUnreconciled } from "./SourceUnreconciled";

/**
 * §B3. The route element for `/migrations/:migrationId/scope`.
 *
 * It owns every query and every mutation, the read-only decision and the sign-off dialog. Three rules are worth
 * reading before changing anything here:
 *
 * 1. **The basis is not a scope key** (§B3.2). `conversion_date` and `base_currency` are columns on `migrations`
 *    and go through `update_conversion_basis` (endpoint 25). Putting them in the scope patch would be rejected
 *    by §B3.3's key set, and correctly so.
 * 2. **The client never writes the actor of an override** (§B3.3). `allow_non_empty`, `accept_warnings` and
 *    `allow_unbalanced` are the operator's to set and the **API's** to attribute: it writes
 *    `allow_non_empty_actor = operator.name` in the same patch. A body that supplies it is `validation_failed`,
 *    and rightly — an override whose attribution came from the browser is not an audit trail.
 * 3. **After sign-off there is no second call** (§B3.4, §A9.5). `signoff_migration` chains into
 *    `advance_migration` itself, so the screen stops mutating and starts polling; a follow-up `run` would race
 *    the chain and enqueue a second job against the `queueing_lock` for no reason.
 * 4. **Before the first run there IS one call** — and only here. `create_migration` defers nothing (§A9.5), so a
 *    freshly created migration sits in `draft` and `draft -> connecting` is an EXPLICIT transition
 *    (`workflow/states.py`): without this button the profile is never built, the preflight table is empty for
 *    ever and sign-off can never enable. It is the API equivalent of the CLI's `run_until_signoff` after
 *    `MigrationRepo.create` (`cli/main.py::demo`). This is a single `run_migration`, deferred once, and it is
 *    NOT the "second run click" §C2.3 step 4 forbids — that one is after sign-off.
 */

/** §B3.3, verbatim and complete. Exported so `tests/scope.test.tsx` asserts it rather than trusting it. */
export const SCOPE_KEYS = [
  "source_params",
  "destination_params",
  "auto_approve_mapping",
  "allow_non_empty",
  "allow_non_empty_actor",
  "history_tier",
  "entities",
  "extracted_as_at",
  "accept_warnings",
  "allow_unbalanced",
  "fy_end_policy",
  // Opus review finding E (HIGH), Phase 3 spec §9: the ONLY writer for `due_migration_ids`' gate --
  // without this key set, the periodic attachment tick never picks a migration up at all.
  "attachments",
  // MT-17 design §5/§8: the operator's declared, per-bank-account unreconciled figure. Its OWN section
  // (`SourceUnreconciled`) sends it in a PATCH of its own -- never the general "Save scope" body below,
  // see `onSave` (UI9).
  "source_unreconciled",
] as const;

/** What this screen may put in a PATCH body: §B3.3's set minus the actor, which only the API writes. */
export const CLIENT_WRITABLE_SCOPE_KEYS: readonly string[] = SCOPE_KEYS.filter(
  (key) => key !== "allow_non_empty_actor",
);

const HISTORY_TIERS = ["T0", "T1", "T2", "T3"] as const;

type ScopeValues = {
  entities: string[];
  history_tier: string;
  auto_approve_mapping: boolean;
  allow_non_empty: boolean;
  accept_warnings: boolean;
  allow_unbalanced: boolean;
  fy_end_policy: string;
  attachments: boolean;
  extracted_as_at: string;
  source_params: string;
  destination_params: string;
};

function asObjectText(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function parseObject(text: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(text === "" ? "{}" : text);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function fromScope(scope: Record<string, unknown> | undefined): ScopeValues {
  const s = scope ?? {};
  return {
    entities: Array.isArray(s.entities) ? (s.entities as string[]) : [],
    history_tier: typeof s.history_tier === "string" ? s.history_tier : "T0",
    auto_approve_mapping: s.auto_approve_mapping === true,
    allow_non_empty: s.allow_non_empty === true,
    accept_warnings: s.accept_warnings === true,
    allow_unbalanced: s.allow_unbalanced === true,
    // MT-17 design §1.3/critique B23: no default. A save with none chosen used to send "roll" on every
    // Scope-screen save, which pre-answered the CONVERSION_AT_FY_END preflight question before the
    // operator ever chose one -- and, once the connector honours `scope.fy_end_policy` (this design), it
    // would also silently roll a MID-YEAR conversion's P&L into retained earnings.
    fy_end_policy: typeof s.fy_end_policy === "string" ? s.fy_end_policy : "",
    attachments: s.attachments === true,
    extracted_as_at: typeof s.extracted_as_at === "string" ? s.extracted_as_at : "",
    source_params: asObjectText(s.source_params),
    destination_params: asObjectText(s.destination_params),
  };
}

/**
 * F2/final decision 15: a `validation_failed` 422's `details.fields[].loc` marks the row named by
 * `loc[2]` for BOTH shapes -- the pydantic value error `["body","source_unreconciled",<key>]` and the
 * key error/W14 `[...,<key>,"[key]"]` -- so only `loc[1]` (not the array's length) tells this section's
 * fields apart from every other scope field a 422 could also be naming. Defensive about shape: a caught
 * error that is not the §A3 envelope, or `details.fields` that is missing or malformed, yields `{}`
 * rather than throwing -- same posture as `describeApiError`.
 */
function sourceUnreconciledFieldErrors(caught: unknown): Record<string, string> {
  if (!isApiError(caught) || caught.code !== "validation_failed") return {};
  const fields = caught.details.fields;
  if (!Array.isArray(fields)) return {};
  const out: Record<string, string> = {};
  for (const entry of fields) {
    if (typeof entry !== "object" || entry === null) continue;
    const rec = entry as Record<string, unknown>;
    const loc = rec.loc;
    if (!Array.isArray(loc) || loc[1] !== "source_unreconciled" || typeof loc[2] !== "string") continue;
    out[loc[2]] = typeof rec.msg === "string" ? rec.msg : t("error.unexpected");
  }
  return out;
}

/**
 * The label is the **translated string**, not the key. 06a's `tests/i18n.test.ts` finds a reference by matching
 * a quoted literal in the translate call, so a key held in a data table and cast at the call site reads as
 * unreferenced and fails the "(b) every key is referenced" assertion for the whole `scope.profile_*` family.
 * Calling `t` here at module scope is safe: `en.ts` is a plain frozen object and there is no locale switch in 1d.
 */
const PROFILE_ROWS: readonly { key: string; label: string }[] = [
  { key: "base_currency", label: t("scope.profile_base_currency") },
  { key: "fy_end", label: t("scope.profile_fy_end") },
  { key: "vat_number", label: t("scope.profile_vat_number") },
  { key: "vat_scheme", label: t("scope.profile_vat_scheme") },
  { key: "vat_filing_frequency", label: t("scope.profile_vat_frequency") },
  { key: "flat_rate_percent", label: t("scope.profile_flat_rate") },
  { key: "lock_dates", label: t("scope.profile_lock_dates") },
  { key: "role", label: t("scope.profile_role") },
  { key: "plan_tier", label: t("scope.profile_plan_tier") },
  { key: "is_empty", label: t("scope.profile_is_empty") },
];

export function ScopeScreen(): JSX.Element {
  const { migrationId } = useParams();
  const id = migrationId ?? "";

  const systems = useSystems();
  const migration = useMigration(id === "" ? null : id);
  const scope = useScope(id);
  const profile = useProfile(id);
  const capabilities = useCapabilities(id);
  const consent = useConsent(id);
  const updateScope = useUpdateScope(id);
  const updateBasis = useUpdateConversionBasis(id);
  const signoff = useSignoff(id);
  const signoffWatch = useSignoffWatch(id, migration.data?.state);
  const signoffBusy = signoff.isPending || signoffWatch.waiting;
  const run = useRun(id);
  // FX opening-balance spec §3.7: the FX section's own query and three mutations, owned here like every
  // other query/mutation on this screen.
  const fx = useFxOpeningBalances(id);
  // The header's h1 is the client organisation (Scope.html), from the source connection.
  const connections = useConnections({ includeSuperseded: true });
  const setFxBalance = useSetFxOpeningBalance(id);
  const acceptFxEstimate = useAcceptFxOpeningEstimate(id);
  const restoreFxAuto = useRestoreFxOpeningAutoFigure(id);
  const resolveFxFinding = useResolveReviewItem(id);
  // XMG scope-blockers: every open BLOCK review item the server counts against the scope sign-off
  // (`open_blocking_reviews`: the CURRENT mapping version or none, BLOCK, unresolved) -- the default
  // `mapping_version=current` is that same set. A SOURCE item filed at scope time (e.g.
  // `fixed_asset_depreciation_not_aligned`) is answered HERE; the Review screen is not this stage's screen.
  const reviewItems = useAllReviewItems(id, {});
  const resolveBlocking = useResolveReviewItem(id);
  const qc = useQueryClient();

  // The server's refusal of the last sign-off attempt, shown beside the button (the top banner is off-screen
  // once the operator has scrolled down to the sign-off card).
  const [signoffError, setSignoffError] = useState<string | null>(null);
  const [blockingFailure, setBlockingFailure] = useState<string | null>(null);
  const signoffErrorRef = useRef<HTMLDivElement | null>(null);
  const [values, setValues] = useState<ScopeValues | null>(null);
  // 6.1: the two segmented tabs. Tab 1 "Scope" is profiles/preflight/entities/overrides/sign-off; tab 2
  // "Opening balances" is the FX opening balances and the unreconciled bank activity.
  const [tab, setTab] = useState<"scope" | "fx">("scope");
  const [conversionDate, setConversionDate] = useState<string | null>(null);
  const [baseCurrency, setBaseCurrency] = useState<string | null>(null);
  const [paramsError, setParamsError] = useState<string | null>(null);
  // The Overrides section opens when a save rejects the JSON params and stays open until the operator closes it
  // (fixing the JSON and saving must not collapse it under their hands).
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [frozenByError, setFrozenByError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [signedOff, setSignedOff] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // F2/final decision 15: a 422's `details.fields[].loc[2]` on `source_unreconciled`, keyed by account id.
  const [unreconciledFieldErrors, setUnreconciledFieldErrors] = useState<Record<string, string>>({});

  // The server's values are the source of truth; local state is an edit buffer seeded from them. `edited`
  // tracks whether the operator has changed anything in that buffer since it was last seeded (`set()`
  // below sets it; a (re-)seed clears it) -- LOW fix: the buffer used to seed from `scope.data` exactly
  // ONCE and never again, so a state transition that refetches a fresher scope (e.g. `stage_scope`
  // finishing and writing a real `extracted_as_at`/`entities`/`source_unreconciled`) never reached the
  // buffer, and a later "Save scope" click could PATCH the stale, pre-transition values straight back
  // over what the worker just wrote.
  const editedRef = useRef(false);
  // Round-2 review finding (HIGH): nulling `values` below does not itself guarantee the next render sees
  // fresh data -- `invalidateQueries` only starts a background refetch, and React re-renders immediately
  // with `scope.data` still pointing at the pre-transition object while that refetch is in flight. This
  // effect used to treat "values is null and scope.data is defined" as "safe to seed", so it re-seeded
  // from the STALE object one render before the fresher one landed, and then never got another chance
  // (`values` was no longer null once the real data arrived). `awaitingFreshScopeRef`/`staleScopeDataRef`
  // make a post-transition (re-)seed wait for a `scope.data` reference that differs from the one captured
  // at the transition, and for that refetch to have actually settled, before treating it as safe to seed.
  const awaitingFreshScopeRef = useRef(false);
  const staleScopeDataRef = useRef<typeof scope.data>(undefined);
  useEffect(() => {
    if (scope.data === undefined || values !== null) return;
    if (awaitingFreshScopeRef.current) {
      if (scope.isFetching || scope.data === staleScopeDataRef.current) return;
      awaitingFreshScopeRef.current = false;
    }
    setValues(fromScope(scope.data.scope));
    editedRef.current = false;
  }, [scope.data, scope.isFetching, values]);

  const state = migration.data?.state as MigrationState | undefined;
  // `draft`/`connecting` map to the CONNECT stage, so `isStageActive` is false there — but that is the state a
  // migration is in the moment the consent form navigates here, and it is the one state the operator must be
  // able to act on. It is "not started", not "out of order".
  const notStarted = state === "draft" || state === "connecting";
  const stageActive = state === undefined ? true : isStageActive(state, "scope") || notStarted;
  const frozen = scope.data?.frozen === true || frozenByError;
  const readOnly = frozen || !stageActive;
  // xmg-webux (defect 3): the server signs a scope off only from `awaiting_scope_signoff`.
  // Signed off but the state has not moved yet: the button stays, as "Working" (or, once the window ran out, usable again).
  const signoffOpen =
    state === "awaiting_scope_signoff" &&
    !frozen &&
    (!signedOff || signoffWatch.waiting || signoffWatch.stalled);
  const signoffPending = notStarted || state === "profiling" || state === "scoping";

  // Critique-round finding (LOW): `useScope` has no poll of its own, so once `stage_scope` finishes and the
  // polled `migration` query picks up the `-> awaiting_scope_signoff` transition, the scope query can still
  // be serving the pre-transition response (`source_bank_accounts: null`) -- the section would then tell the
  // operator this source cannot list its accounts at all, which is wrong, only stale. Re-fetch the scope
  // (and the fields it feeds, profile/capabilities -- neither of which has a poll of its own, so both are
  // exactly as stale as the scope query was before this fix) whenever the polled state actually changes.
  const previousState = useRef<MigrationState | undefined>(undefined);
  useEffect(() => {
    if (previousState.current !== undefined && previousState.current !== state) {
      // xmg-webux: the refetch itself is `MigrationPage`'s `useLiveMigrationRefresh` (it invalidates every
      // query under this migration on the same state change, scope/profile/capabilities included); this
      // effect only re-arms the edit buffer below. Invalidating here too fetched the scope twice.
      // LOW fix (see `editedRef`): only drop the buffer when it holds no unsaved edits -- an operator
      // mid-edit must never lose that work to a background refetch. Nulling it here re-triggers the seed
      // effect above, which (per the HIGH fix above) waits for that invalidated scope query to actually
      // resolve with a fresher object before it seeds -- `scope.data` at THIS instant is still the
      // pre-transition object, so it is what "stale" is measured against.
      if (!editedRef.current) {
        awaitingFreshScopeRef.current = true;
        staleScopeDataRef.current = scope.data;
        setValues(null);
      }
    }
    previousState.current = state;
  }, [state, scope.data]);
  // A refused sign-off must be seen: bring its message into view and focus it for screen readers.
  useEffect(() => {
    if (signoffError !== null) {
      signoffErrorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      signoffErrorRef.current?.focus({ preventScroll: true });
    }
  }, [signoffError]);
  // Same staleness, for the section's OWN "not available yet" vs. "this source cannot list its accounts"
  // copy: `profiling`/`scoping` haven't reached `stage_scope` either, so they read exactly like "not
  // started" here, never like a connector that genuinely has no list.
  const unreconciledListPending = notStarted || state === "profiling" || state === "scoping";

  // `ProfileOut` names the list `issues` (§A8 row 26); there is no `preflight` field on it.
  const preflight: PreflightItem[] = useMemo(() => profile.data?.issues ?? [], [profile.data]);
  const blocking = preflight.filter((item) => (item.severity as Severity) === "BLOCK");
  // The review items that refuse the scope sign-off. FX_OPENING ones are answered on the FX tab (they carry
  // amounts, not just an acknowledgement) and counted from `fx.blocking`; every other kind is answered in the
  // panel below.
  const gatingItems = (reviewItems.data ?? []).filter((item) => item.severity === "BLOCK" && isOpen(item));
  // Only at the scope sign-off itself: earlier (profiling/scoping) nothing refuses yet, and later stages own theirs.
  const blockingPanelOpen = state === "awaiting_scope_signoff";
  const gatingHere = blockingPanelOpen ? gatingItems.filter((item) => item.kind !== "FX_OPENING") : [];

  if (migration.isPending || scope.isPending) return <Spinner />;

  const current = values ?? fromScope(scope.data?.scope);
  const entries = (capabilities.data?.entries ?? {}) as Record<string, CapabilityEntry>;
  const storedActor = (scope.data?.scope as Record<string, unknown> | undefined)?.allow_non_empty_actor;
  // MT-17 design §5/critique B3: `undefined` is treated exactly like `null`, and a stored value that is
  // not an object (legacy/malformed data) is shown as `{}` rather than crashing the screen.
  const rawUnreconciled = (scope.data?.scope as Record<string, unknown> | undefined)?.source_unreconciled;
  const declaredUnreconciled: Record<string, string> =
    rawUnreconciled !== null && typeof rawUnreconciled === "object" && !Array.isArray(rawUnreconciled)
      ? (rawUnreconciled as Record<string, string>)
      : {};
  // Round-2 review finding (MEDIUM): the section's hint must read the SERVER's `extracted_as_at`, never
  // `current.extracted_as_at` (the `values` edit buffer). That buffer is seeded from `scope.data` only
  // ONCE (`values === null`, above) and is never re-seeded when the staleness-fix effect refetches the
  // scope after a state change -- so once the buffer has seeded (even to "" during `scoping`), a later,
  // real `extracted_as_at` written by `stage_scope` never reaches the hint. Server data sidesteps the
  // buffer entirely and matches UI1 ("a hint with ... `extracted_as_at`"), which is about what the
  // source declares, not what the operator happens to be mid-editing.
  const rawExtractedAsAt = (scope.data?.scope as Record<string, unknown> | undefined)?.extracted_as_at;
  const serverExtractedAsAt =
    typeof rawExtractedAsAt === "string" && rawExtractedAsAt !== "" ? rawExtractedAsAt : null;

  function set<K extends keyof ScopeValues>(key: K, value: ScopeValues[K]): void {
    editedRef.current = true;
    setValues({ ...current, [key]: value });
  }

  async function onSave(): Promise<void> {
    setParamsError(null);
    setSaved(false);
    const sourceParams = parseObject(current.source_params);
    const destinationParams = parseObject(current.destination_params);
    if (sourceParams === null || destinationParams === null) {
      setParamsError(t("scope.params_invalid"));
      // Opened here, not by an effect on the error: the same bad JSON saved twice reopens it too.
      setOverridesOpen(true);
      return;
    }
    // Exactly §B3.3's client-writable set, and nothing else. `allow_non_empty_actor` is the API's to write.
    const body: Record<string, unknown> = {
      source_params: sourceParams,
      destination_params: destinationParams,
      auto_approve_mapping: current.auto_approve_mapping,
      allow_non_empty: current.allow_non_empty,
      history_tier: current.history_tier,
      entities: current.entities,
      extracted_as_at: current.extracted_as_at === "" ? null : current.extracted_as_at,
      accept_warnings: current.accept_warnings,
      allow_unbalanced: current.allow_unbalanced,
      attachments: current.attachments,
    };
    // MT-17 design §1.3/critique B23: the PATCH omits `fy_end_policy` entirely until the operator has
    // actually chosen one -- sending "" (or the old silent "roll" default) would either fail validation
    // or pre-answer a question nobody was asked.
    if (current.fy_end_policy !== "") {
      body.fy_end_policy = current.fy_end_policy;
    }
    try {
      if (conversionDate !== null || baseCurrency !== null) {
        // §B3.2: the basis is columns, not scope keys, and goes to endpoint 25.
        await updateBasis.mutateAsync({
          ...(conversionDate === null ? {} : { conversion_date: conversionDate }),
          ...(baseCurrency === null ? {} : { base_currency: baseCurrency }),
        });
      }
      await updateScope.mutateAsync(body);
      setSaved(true);
      editedRef.current = false;
    } catch (caught) {
      if (isApiError(caught) && caught.code === "scope_frozen") {
        // §B3.2: from EITHER endpoint, the whole screen goes read-only.
        setFrozenByError(true);
        return;
      }
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  async function onStart(): Promise<void> {
    setFailure(null);
    try {
      await run.mutateAsync({});
    } catch (caught) {
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  async function onSignoff(): Promise<void> {
    setConfirming(false);
    if (signoffBusy) return;
    setSignoffError(null);
    try {
      await signoff.mutateAsync({});
      // §B3.4/§A9.5: nothing else is called. The task chains `advance_migration` itself; the screen polls.
      setSignedOff(true);
    } catch (caught) {
      setSignedOff(false);
      setSignoffError(describeApiError(caught, t("error.unexpected")));
      void qc.invalidateQueries({ queryKey: ["migration", id] });
    }
  }

  async function onResolveBlocking(itemId: string, option: string): Promise<void> {
    setBlockingFailure(null);
    setSignoffError(null);
    try {
      await resolveBlocking.mutateAsync({
        itemId,
        body: { option },
        mayMoveState: resolveMayMoveState(state ?? "draft", option),
      });
    } catch (caught) {
      setBlockingFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  async function onSaveUnreconciled(map: Record<string, string>): Promise<void> {
    // MT-17 design §5/decision 15: the SAME `updateScope` mutation as the general Save, but a PATCH
    // whose body carries only `source_unreconciled` -- it therefore works before T6 lands (§B3.2's
    // `history_tier` field type gap) and can never overwrite the other keys (I7).
    setUnreconciledFieldErrors({});
    try {
      await updateScope.mutateAsync({ source_unreconciled: map });
      setSaved(true);
    } catch (caught) {
      if (isApiError(caught) && caught.code === "scope_frozen") {
        setFrozenByError(true);
        return;
      }
      // F2: a `migration_busy` 409, or anything else, goes to the same banner every other Save error
      // uses; a `validation_failed` 422 ALSO marks the offending row(s), through `serverFieldErrors`.
      setUnreconciledFieldErrors(sourceUnreconciledFieldErrors(caught));
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  async function onFxSave(accountId: string, amount: string): Promise<void> {
    try {
      await setFxBalance.mutateAsync({ accountId, body: { amount, currency: fxCurrencyOf(accountId) } });
    } catch (caught) {
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  async function onFxClear(accountId: string): Promise<void> {
    try {
      await setFxBalance.mutateAsync({
        accountId,
        body: { amount: null, currency: fxCurrencyOf(accountId) },
      });
    } catch (caught) {
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  async function onFxRestoreAuto(accountId: string): Promise<void> {
    try {
      await restoreFxAuto.mutateAsync({ accountId });
    } catch (caught) {
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  async function onFxAcceptEstimate(accountId: string): Promise<void> {
    try {
      await acceptFxEstimate.mutateAsync({ accountId });
    } catch (caught) {
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  async function onFxAcknowledge(reviewItemId: string, option: string): Promise<void> {
    try {
      await resolveFxFinding.mutateAsync({
        itemId: reviewItemId,
        body: { option },
        mayMoveState: resolveMayMoveState(state ?? "draft", option),
      });
    } catch (caught) {
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  // The PUT body needs the account's OWN currency (§3.5): the server 422s a mismatch, and the operator
  // never has to type it. Falls back to the migration's base currency, which is unreachable in practice --
  // `FxOpeningBalances` only calls this with an account id it just rendered from `fx.data.accounts`.
  function fxCurrencyOf(accountId: string): string {
    return fx.data?.accounts.find((account) => account.account_id === accountId)?.currency ?? "";
  }

  const sourceConnection = (connections.data ?? []).find(
    (row) => row.id === migration.data?.source_connection_id,
  );
  const title = sourceConnection === undefined ? t("scope.title") : describeConnection(sourceConnection).org;
  const meta = t("migrationheader.meta_scope", {
    source: vendorName(migration.data?.source_system ?? ""),
    destination: vendorName(migration.data?.destination_system ?? ""),
    date: formatDate(migration.data?.conversion_date) || "—",
    currency: migration.data?.base_currency ?? "—",
  });
  const fxBlocking = fx.data?.blocking ?? 0;
  const reviewBlockingCount = gatingHere.length + fxBlocking;

  // xmg-actions-first: what each closed section says in its summary.
  const journalsMissing = scope.data?.journals_scope_missing === true;
  const budget = scope.data?.api_budget;
  const apiBudgetCounts =
    budget === null || budget === undefined
      ? undefined
      : budget.est_days <= 1
        ? t("scope.api_budget_counts_one_day", { calls: groupCalls(budget.est_calls) })
        : t("scope.api_budget_counts", { calls: groupCalls(budget.est_calls), days: budget.est_days });
  const warnings = preflight.length - blocking.length;
  const preflightCounts =
    preflight.length === 0
      ? t("scope.preflight_counts_clear")
      : [
          blocking.length === 0 ? null : t("scope.preflight_counts_blocking", { n: blocking.length }),
          warnings === 0 ? null : t("scope.preflight_counts_warnings", { n: warnings }),
        ]
          .filter((part): part is string => part !== null)
          .join(", ");
  const differences = PROFILE_ROWS.filter(
    (row) =>
      String((profile.data?.source as Record<string, unknown>)?.[row.key] ?? "—") !==
      String((profile.data?.destination as Record<string, unknown>)?.[row.key] ?? "—"),
  ).length;
  const profileCounts =
    differences === 0
      ? t("scope.profiles_counts_same")
      : t("scope.profiles_counts_differ", { n: differences });
  const entityCounts = t("scope.entities_counts", {
    n: current.entities.length,
    total: Object.keys(entries).length,
  });
  const basisCounts = [
    conversionDate ?? formatDate(migration.data?.conversion_date),
    baseCurrency ?? migration.data?.base_currency ?? "",
  ]
    .filter((part) => part !== "")
    .join(", ");
  const switchedOn = [
    current.allow_non_empty,
    current.attachments,
    current.accept_warnings,
    current.allow_unbalanced,
    current.auto_approve_mapping,
  ].filter(Boolean).length;
  const overrideCounts =
    switchedOn === 0 ? t("scope.overrides_counts_none") : t("scope.overrides_counts", { n: switchedOn });

  // xmg-actions-first: the open BLOCK questions (and the journals choice) lead; the sign-off sits beside them
  // and stays in view; the settings and checks are collapsed below.
  const questionsNode =
    gatingHere.length === 0 && !(tab === "scope" && journalsMissing) ? null : (
      <>
        {gatingHere.length === 0 ? null : (
          <section
            id="scope-blocking-items"
            data-testid="scope-blocking-items"
            aria-labelledby="scope-blocking-title"
          >
            <h2 id="scope-blocking-title">{t("scope.blocking_items_title")}</h2>
            <p className="muted">{t("scope.blocking_items_note")}</p>
            {blockingFailure === null ? null : <Banner tone="block">{blockingFailure}</Banner>}
            <OtherReviewItems
              items={gatingHere}
              disabled={frozen || signedOff || resolveBlocking.isPending}
              onResolve={(itemId, option) => void onResolveBlocking(itemId, option)}
            />
          </section>
        )}
        {tab === "scope" ? (
          <JournalsChoice
            migrationId={id}
            scope={scope.data}
            frozen={frozen}
            conversionDate={formatDate(migration.data?.conversion_date) || null}
            uploadOpen={
              !frozen || state === "awaiting_mapping_review" || state === "awaiting_conversion_signoff"
            }
          />
        ) : null}
      </>
    );
  const actionNode = (
    <>
      <Card title={t("scope.signoff")}>
        {/* xmg-webux (defect 3): the sign-off action exists only while the migration is waiting for
                  it. Before (draft .. scoping) and after (frozen, any later state) the card says why there is
                  nothing to sign instead of showing a dead button next to "shown read-only". */}
        {signoffOpen ? (
          <>
            <Button
              variant="primary"
              className="button--auth"
              onClick={() => setConfirming(true)}
              disabled={blocking.length > 0 || reviewBlockingCount > 0 || signoffBusy}
            >
              {signoffBusy ? t("signoff.working") : t("scope.signoff")}
            </Button>
            {signoffWatch.stalled ? (
              <span className="muted" role="status" data-testid="signoff-stalled">
                {t("signoff.still_processing")}
              </span>
            ) : null}
            {reviewBlockingCount === 0 ? null : (
              <span className="failnote" data-testid="scope-signoff-blocking-hint">
                {reviewBlockingCount === 1
                  ? t("scope.signoff_blocking_one")
                  : t("scope.signoff_blocking_many", { count: reviewBlockingCount })}{" "}
                {gatingHere.length === 0 ? null : (
                  <a href="#scope-blocking-items">{t("scope.signoff_blocking_link")}</a>
                )}
                {fxBlocking === 0 ? null : (
                  <Button className="button--wrap" onClick={() => setTab("fx")}>
                    {t("scope.signoff_blocking_fx_link")}
                  </Button>
                )}
              </span>
            )}
            {signoffError === null ? null : (
              <div ref={signoffErrorRef} tabIndex={-1} data-testid="scope-signoff-error" role="alert">
                <Banner tone="block" title={t("scope.signoff_error_title")}>
                  {signoffError}
                </Banner>
              </div>
            )}
            {blocking.length === 0 ? null : (
              <span className="failnote">{t("scope.signoff_blocked_hint", { count: blocking.length })}</span>
            )}
          </>
        ) : (
          <p className="asidenote" data-testid="scope-signoff-closed">
            {frozen || signedOff
              ? t("scope.signoff_closed_done")
              : signoffPending
                ? t("scope.signoff_closed_pending")
                : t("scope.signoff_closed")}
          </p>
        )}
        {blocking.length === 0 || signoffOpen ? null : (
          <span className="failnote">{t("scope.signoff_blocked_hint", { count: blocking.length })}</span>
        )}
        {readOnly ? null : (
          <Button className="button--auth" onClick={() => void onSave()} disabled={updateScope.isPending}>
            {t("scope.save")}
          </Button>
        )}
      </Card>
      <p className="asidenote afaction__hint">{t("scope.signoff_note")}</p>
    </>
  );

  return (
    <MigrationPage
      id={id}
      migration={migration.data}
      title={title}
      meta={meta}
      announce={saved ? t("scope.saved") : undefined}
    >
      <SystemWarningBanner
        warnings={systems.data?.warnings}
        systems={[migration.data?.source_system, migration.data?.destination_system]}
      />
      {frozen ? <Banner tone="info">{t("scope.frozen")}</Banner> : null}
      {!stageActive && !frozen ? <Banner tone="info">{t("scope.not_active")}</Banner> : null}
      {notStarted && !frozen ? (
        <section aria-labelledby="scope-start">
          <h2 id="scope-start">{t("scope.start")}</h2>
          <Banner tone="info">{t("scope.not_started")}</Banner>
          <Button variant="primary" onClick={() => void onStart()} disabled={run.isPending}>
            {t("scope.start")}
          </Button>
        </section>
      ) : null}
      {failure === null ? null : <Banner tone="block">{failure}</Banner>}
      {signedOff ? (
        <Banner tone="info">{t("scope.signoff_done", { hash: scope.data?.scope_sha256 ?? "" })}</Banner>
      ) : null}

      <ActionsFirst
        questions={questionsNode}
        action={actionNode}
        actionLabel={t("scope.signoff")}
        pinned={signoffOpen}
      >
        <SegmentedTabs
          label={t("scope.tabs_label")}
          currentId={tab}
          onSelect={(next) => setTab(next === "fx" ? "fx" : "scope")}
          tabs={[
            {
              id: "scope",
              label: t("scope.tab_scope"),
              ...(blocking.length > 0 ? { count: blocking.length } : {}),
            },
            { id: "fx", label: t("scope.tab_fx"), ...(fxBlocking > 0 ? { count: fxBlocking } : {}) },
          ]}
        />

        {tab === "fx" ? (
          <>
            {/* The FX panel owns its own blocking banner (fx.blocking) and findings. */}
            {fx.data === undefined ? null : (
              <FxOpeningBalances
                data={fx.data}
                migrationId={id}
                scopeFrozen={frozen}
                saving={
                  setFxBalance.isPending ||
                  acceptFxEstimate.isPending ||
                  restoreFxAuto.isPending ||
                  resolveFxFinding.isPending
                }
                onSave={(accountId, amount) => void onFxSave(accountId, amount)}
                onClear={(accountId) => void onFxClear(accountId)}
                onAcceptEstimate={(accountId) => void onFxAcceptEstimate(accountId)}
                onRestoreAuto={(accountId) => void onFxRestoreAuto(accountId)}
                onAcknowledge={(reviewItemId, option) => void onFxAcknowledge(reviewItemId, option)}
              />
            )}
            <SourceUnreconciled
              accounts={scope.data?.source_bank_accounts ?? null}
              declared={declaredUnreconciled}
              unknownKeys={scope.data?.source_unreconciled_unknown_keys ?? null}
              derived={scope.data?.source_unreconciled_derived ?? null}
              origin={scope.data?.source_unreconciled_origin ?? null}
              baseCurrency={migration.data?.base_currency ?? ""}
              extractedAsAt={serverExtractedAsAt}
              // Same class as `extractedAsAt` above: the hint must show the SAVED conversion date, not an
              // unsaved edit in the Basis card's date input -- typing there (or clearing it) must
              // never change what this hint claims the declared figures are "as at".
              conversionDate={formatDate(migration.data?.conversion_date) || null}
              state={state}
              notStarted={unreconciledListPending}
              frozen={frozen}
              saving={updateScope.isPending}
              serverFieldErrors={unreconciledFieldErrors}
              onSave={(map) => void onSaveUnreconciled(map)}
            />
          </>
        ) : (
          <>
            {scope.data?.api_budget === null || scope.data?.api_budget === undefined ? null : (
              <Disclosure
                asHeading
                title={t("scope.api_budget_title")}
                counts={apiBudgetCounts}
                testId="scope-api-budget-section"
              >
                <ApiBudgetNotice budget={scope.data?.api_budget} bare />
              </Disclosure>
            )}
            <Disclosure
              asHeading
              title={t("scope.preflight")}
              counts={preflightCounts}
              open={blocking.length > 0 ? true : undefined}
              attention={blocking.length > 0}
              testId="scope-preflight-section"
            >
              <p className="muted">{t("scope.preflight_caption")}</p>
              {preflight.length === 0 ? (
                <p className="card__body">{t("scope.preflight_clear")}</p>
              ) : (
                <div className="stacktable">
                  <Table<PreflightItem>
                    bare
                    wrap
                    caption={t("scope.preflight_caption")}
                    rowKey={(row) => `${row.severity}:${row.check}`}
                    rows={preflight}
                    columns={[
                      {
                        key: "severity",
                        header: t("scope.col_severity"),
                        render: (row) => (
                          <Badge tone={row.severity === "BLOCK" ? "fail" : "warn"} dot>
                            {t(`severity.${row.severity as Severity}` as "severity.BLOCK")}
                          </Badge>
                        ),
                      },
                      {
                        key: "check",
                        header: t("scope.col_check"),
                        render: (row) => <span className="cellvendor__name">{row.check}</span>,
                      },
                      {
                        key: "source",
                        header: t("scope.col_source"),
                        render: (row) => <span className="mono">{String(row.source_value ?? "—")}</span>,
                      },
                      {
                        key: "destination",
                        header: t("scope.col_destination"),
                        render: (row) => <span className="mono">{String(row.destination_value ?? "—")}</span>,
                      },
                      // Verbatim: §B3.1 requires the reason to say which side must change.
                      // `PreflightIssueOut` carries the sentence in `message`; there is no `reason` column.
                      {
                        key: "reason",
                        header: t("scope.col_reason"),
                        cellClassName: "cell--wide",
                        render: (row) => <span className="cellwrap muted">{row.message}</span>,
                      },
                    ]}
                  />
                </div>
              )}
              {blocking.length === 0 ? null : (
                <div className="card__body">
                  <Banner tone="block">{t("scope.blocked", { count: blocking.length })}</Banner>
                </div>
              )}
            </Disclosure>

            <Disclosure
              asHeading
              title={t("scope.profiles")}
              counts={profileCounts}
              testId="scope-profiles-section"
            >
              <div className="kv" role="table" aria-label={t("scope.profiles")}>
                {/* display:contents keeps the design's flat 3-column grid while the ARIA table roles nest
                    properly (axe: a table must own rows, a row must own cells). */}
                <span className="kv__row" role="row">
                  <span className="kv__head eyebrow" role="columnheader">
                    {t("scope.col_field")}
                  </span>
                  <span className="kv__head eyebrow" role="columnheader">
                    {t("scope.profile_source")} · {vendorName(migration.data?.source_system ?? "")}
                  </span>
                  <span className="kv__head eyebrow" role="columnheader">
                    {t("scope.profile_destination")} · {vendorName(migration.data?.destination_system ?? "")}
                  </span>
                </span>
                {PROFILE_ROWS.map((row) => {
                  const sourceValue = String(
                    (profile.data?.source as Record<string, unknown>)?.[row.key] ?? "—",
                  );
                  const destinationValue = String(
                    (profile.data?.destination as Record<string, unknown>)?.[row.key] ?? "—",
                  );
                  const mismatch = sourceValue !== destinationValue;
                  return (
                    <span className="kv__row" role="row" key={row.key}>
                      <span className="muted" role="cell">
                        {row.label}
                      </span>
                      <span className="mono" role="cell">
                        {sourceValue}
                      </span>
                      <span
                        role="cell"
                        className={
                          mismatch ? (row.key === "base_currency" ? "mono numfail" : "mono numwarn") : "mono"
                        }
                      >
                        {destinationValue}
                      </span>
                    </span>
                  );
                })}
              </div>
            </Disclosure>

            <Disclosure
              asHeading
              title={t("scope.entities")}
              counts={entityCounts}
              testId="scope-entities-section"
            >
              <p className="muted">{t("scope.entities_caption")}</p>
              <EntityToggles
                entries={entries}
                selected={current.entities}
                disabled={readOnly}
                onToggle={(entity, include) =>
                  set(
                    "entities",
                    include ? [...current.entities, entity] : current.entities.filter((e) => e !== entity),
                  )
                }
              />
              <div className="card__body">
                <Field label={t("scope.extracted_as_at")}>
                  {({ id: fieldId, describedBy }) => (
                    <input
                      id={fieldId}
                      type="date"
                      aria-describedby={describedBy}
                      disabled={readOnly}
                      value={current.extracted_as_at}
                      onChange={(event) => set("extracted_as_at", event.target.value)}
                    />
                  )}
                </Field>
              </div>
            </Disclosure>

            <Disclosure asHeading title={t("scope.basis")} counts={basisCounts} testId="scope-basis-section">
              <div className="fgrid">
                <Field label={t("consent.conversion_date")}>
                  {({ id: fieldId, describedBy }) => (
                    <input
                      id={fieldId}
                      type="date"
                      aria-describedby={describedBy}
                      disabled={readOnly}
                      value={conversionDate ?? formatDate(migration.data?.conversion_date)}
                      onChange={(event) => setConversionDate(event.target.value)}
                    />
                  )}
                </Field>
                <Field label={t("consent.base_currency")}>
                  {({ id: fieldId, describedBy }) => (
                    <input
                      id={fieldId}
                      maxLength={3}
                      aria-describedby={describedBy}
                      disabled={readOnly}
                      value={baseCurrency ?? migration.data?.base_currency ?? ""}
                      onChange={(event) => setBaseCurrency(event.target.value.toUpperCase())}
                    />
                  )}
                </Field>
              </div>
            </Disclosure>

            <Disclosure
              asHeading
              title={t("scope.history_tier")}
              counts={current.history_tier}
              testId="scope-history-section"
            >
              <fieldset>
                <legend className="visually-hidden">{t("scope.history_tier")}</legend>
                {HISTORY_TIERS.map((tier) => (
                  <label key={tier} className="chk">
                    <input
                      type="radio"
                      name="history_tier"
                      value={tier}
                      checked={current.history_tier === tier}
                      // T1-T3 are phase 2 (§B3.2): rendered, disabled, with the reason said out loud.
                      disabled={readOnly || tier !== "T0"}
                      onChange={() => set("history_tier", tier)}
                    />
                    {tier === "T0"
                      ? t("scope.history_tier_t0")
                      : `${tier} — ${t("scope.history_tier_disabled")}`}
                  </label>
                ))}
              </fieldset>
            </Disclosure>

            <Disclosure
              asHeading
              title={t("scope.overrides")}
              counts={overrideCounts}
              open={overridesOpen}
              onOpenChange={setOverridesOpen}
              attention={paramsError !== null}
              testId="scope-overrides-section"
            >
              <div>
                <label className="chk">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={current.allow_non_empty}
                    onChange={(event) => set("allow_non_empty", event.target.checked)}
                  />
                  <span>
                    <span className="chk__label">{t("scope.allow_non_empty")}</span>
                    <span className="chk__sub">{t("scope.allow_non_empty_sub")}</span>
                  </span>
                </label>
                <label className="chk">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={current.attachments}
                    onChange={(event) => set("attachments", event.target.checked)}
                  />
                  {t("scope.attachments")}
                </label>
                <label className="chk">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={current.accept_warnings}
                    onChange={(event) => set("accept_warnings", event.target.checked)}
                  />
                  {t("scope.accept_warnings")}
                </label>
                <label className="chk">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={current.allow_unbalanced}
                    onChange={(event) => set("allow_unbalanced", event.target.checked)}
                  />
                  {t("scope.allow_unbalanced")}
                </label>
                <label className="chk">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={current.auto_approve_mapping}
                    onChange={(event) => set("auto_approve_mapping", event.target.checked)}
                  />
                  {t("scope.auto_approve_mapping")}
                </label>
                {typeof storedActor === "string" && storedActor !== "" ? (
                  // The API wrote this, in the same patch that set the flag (§B3.3). The client only displays it.
                  <p className="muted">{t("scope.override_actor", { actor: storedActor })}</p>
                ) : null}
                <fieldset>
                  <legend>{t("scope.fy_end_policy")}</legend>
                  {/* xmg-orgadopt item 6: roll is the default; the hint says so before the choice. */}
                  <p className="muted">{t("scope.fy_end_default_hint")}</p>
                  {(["roll", "carry"] as const).map((policy) => (
                    <label key={policy} className="chk">
                      <input
                        type="radio"
                        name="fy_end_policy"
                        value={policy}
                        disabled={readOnly}
                        checked={current.fy_end_policy === policy}
                        onChange={() => set("fy_end_policy", policy)}
                      />
                      {policy === "roll" ? t("scope.fy_end_roll") : t("scope.fy_end_carry")}
                    </label>
                  ))}
                </fieldset>
                <Field label={t("scope.source_params")} hint={t("scope.params_hint")} error={paramsError}>
                  {({ id: fieldId, describedBy }) => (
                    <textarea
                      id={fieldId}
                      aria-describedby={describedBy}
                      disabled={readOnly}
                      value={current.source_params}
                      onChange={(event) => set("source_params", event.target.value)}
                    />
                  )}
                </Field>
                <Field label={t("scope.destination_params")} hint={t("scope.params_hint")}>
                  {({ id: fieldId, describedBy }) => (
                    <textarea
                      id={fieldId}
                      aria-describedby={describedBy}
                      disabled={readOnly}
                      value={current.destination_params}
                      onChange={(event) => set("destination_params", event.target.value)}
                    />
                  )}
                </Field>
              </div>
            </Disclosure>

            <Concepts rows={(capabilities.data?.concepts ?? []) as unknown as readonly ConceptRow[]} />
            <Disclosure
              asHeading
              title={t("scope.signoff_summary")}
              counts={current.history_tier}
              testId="scope-signing-section"
            >
              <dl className="preview__dl">
                <dt>{t("scope.history_tier")}</dt>
                <dd>{current.history_tier === "T0" ? t("scope.history_tier_t0") : current.history_tier}</dd>
                <dt>{t("scope.signoff_year_end")}</dt>
                <dd>
                  {current.fy_end_policy === "roll"
                    ? t("scope.fy_end_roll")
                    : current.fy_end_policy === "carry"
                      ? t("scope.fy_end_carry")
                      : "—"}
                </dd>
                <dt>{t("scope.signoff_consent_label")}</dt>
                <dd>
                  {consent.data === undefined
                    ? t("common.none")
                    : t("scope.signoff_consent", {
                        controller: consent.data.controller_name,
                        signer: consent.data.signer,
                        role: consent.data.signer_role,
                        // `ConsentOut` stamps `accepted_at`; there is no `created_at` on it.
                        date: formatDate(consent.data.accepted_at),
                      })}
                </dd>
              </dl>
              <div className="block">
                <span className="eyebrow">{t("scope.signoff_hash")}</span>
                <code className="hashbox">{scope.data?.scope_sha256 ?? t("common.unknown")}</code>
              </div>
            </Disclosure>
          </>
        )}
      </ActionsFirst>

      <ConfirmDialog
        open={confirming}
        title={t("scope.signoff_confirm_title")}
        onConfirm={() => void onSignoff()}
        onCancel={() => setConfirming(false)}
      >
        <p>{t("scope.signoff_confirm_body")}</p>
        <p>
          <code>{scope.data?.scope_sha256 ?? ""}</code>
        </p>
      </ConfirmDialog>
    </MigrationPage>
  );
}
